import { LegalPage } from "@/components/lunar/LegalPage";

const INTRO = ["By creating a Lunar account or using Lunar, you agree to these Terms of Use."];

const SECTIONS = [
    {
        title: "1. What Lunar is",
        paragraphs: [
            "Lunar is a social app designed to help people connect with other people in real life.",
            "Users may choose to use Lunar for:",
        ],
        list: ["meeting people socially; and/or", "meeting people professionally."],
        afterList: [
            "You choose which experiences you wish to use — Social, Network or both. You will only appear to other users within the experiences you have selected and are actively using.",
            "Lunar makes the introduction — it does not prescribe the relationship. The technology works quietly in the background; then it gets out of the way.",
            "Lunar allows users to check into participating or eligible public places, discover other Lunar users who are open to meeting and, where both users choose to connect, connect with them.",
            "Lunar facilitates introductions. We do not arrange, supervise or participate in meetings between users.",
        ],
    },
    {
        title: "2. Who can use Lunar",
        paragraphs: [
            "You must be 18 or over to create an account or use Lunar.",
            "By creating an account, you confirm that:",
        ],
        list: [
            "you are at least 18 years old;",
            "the information you provide is accurate;",
            "your profile represents you;",
            "you will use Lunar lawfully and responsibly; and",
            "you will comply with these Terms.",
        ],
        afterList: ["You may not create an account for another person without their permission."],
    },
    {
        title: "3. Your account",
        paragraphs: [
            "You are responsible for activity carried out through your Lunar account.",
            "You must not impersonate another person, create deliberately misleading profiles or use another person’s photographs without permission.",
            "You should keep access to your account secure and notify us if you believe somebody else has gained unauthorised access to it.",
            "You may delete your Lunar account using the account deletion function provided within the app.",
        ],
    },
    {
        title: "4. Photos and profile information",
        paragraphs: [
            "Lunar is built around meeting real people in real life. Users must therefore provide a genuine photograph of themselves where requested.",
            "You must not upload content which:",
        ],
        list: [
            "belongs to somebody else without permission;",
            "is deliberately misleading;",
            "contains illegal, threatening, hateful or sexually explicit material;",
            "infringes another person’s rights;",
            "promotes violence or criminal activity; or",
            "is otherwise inappropriate for Lunar.",
        ],
        afterList: [
            "You remain responsible for the content you submit to Lunar.",
            "By uploading content, you give Lunar permission to host, process and display that content where reasonably necessary to operate Lunar. You retain ownership of your content.",
        ],
    },
    {
        title: "5. Social and Network",
        paragraphs: [
            "Lunar provides separate experiences for social and professional connection.",
            "Users control which experiences they enable and may change their choices through their account settings.",
            "Users must respect the context in which another person is participating.",
            "In particular, Network must not be used as a way of attempting unwanted romantic or sexual contact.",
            "Repeatedly contacting somebody for a purpose inconsistent with the experience they have chosen may result in restrictions or removal from Lunar.",
        ],
    },
    {
        title: "6. Location and check-ins",
        paragraphs: [
            "Lunar uses location information to enable certain features, including identifying eligible nearby places and confirming that users are sufficiently close to a place to check in.",
            "A check-in indicates that a user has chosen to make themselves discoverable through Lunar at or in connection with that place for a limited period.",
            "You must not deliberately falsify your location or check into a place where you are not present.",
            "Lunar may restrict the types of locations that can be added or used. Residential addresses, schools and other unsuitable or sensitive locations may be excluded.",
        ],
    },
    {
        title: "7. Meeting other people",
        paragraphs: [
            "Lunar facilitates introductions between users, but you decide whether to communicate with or meet another person.",
            "You should use your own judgement when interacting with or meeting another user. Lunar does not verify that every statement made by another user is true and cannot guarantee another user’s identity, intentions, behaviour or suitability.",
            "For first meetings, we recommend meeting in a public or communal area. You should remain aware of your surroundings and take any other precautions you consider appropriate.",
            "You should not feel obliged to meet or continue interacting with another user simply because you have connected or mutually Waved.",
            "If another user makes you uncomfortable, you can stop interacting with them and use Lunar’s block and report functions.",
            "Nothing in these Terms excludes or limits any responsibility that cannot lawfully be excluded or limited.",
        ],
    },
    {
        title: "8. Waves, connections and messages",
        paragraphs: [
            "Sending a Wave indicates that you are open to connecting with another user or group.",
            "On Social, a Wave is designed to remain private unless both users Wave each other. A recipient may be told that someone there has Waved, but the sender’s identity is not disclosed through that notification.",
            "If both users Wave each other, Lunar enables a connection. A mutual Wave does not create any obligation for either person to communicate or meet.",
            "Network and other Lunar features may operate differently and should not be assumed to be anonymous unless explicitly stated within the relevant feature.",
            "A Wave does not create any obligation on the recipient to respond, connect or meet.",
            "Users must not use Lunar’s communication features to:",
        ],
        list: [
            "harass or intimidate somebody;",
            "repeatedly contact somebody who has indicated they are not interested;",
            "send unsolicited sexual content;",
            "discriminate against or threaten somebody;",
            "advertise or spam users;",
            "solicit money or financial information;",
            "conduct scams or fraudulent activity; or",
            "encourage illegal activity.",
        ],
        afterList: [
            "Lunar is intended to facilitate real-world introductions rather than prolonged online communication. We may therefore place limits on messaging or other communication features.",
        ],
    },
    {
        title: "9. Groups",
        paragraphs: [
            "Users participating in Social may be able to create temporary groups.",
            "Other users must actively join a group through the functionality provided by Lunar, such as a QR code or group code.",
            "You must not add or represent another person as a member of your group without their participation or permission.",
            "Groups may automatically expire when the relevant Lunar check-in ends.",
        ],
    },
    {
        title: "10. Respecting other users",
        paragraphs: [
            "Consent and respect are fundamental to Lunar.",
            "A connection, mutual Wave or other interaction within Lunar does not constitute consent to physical contact, sexual activity or any other interaction.",
            "Users are expected to respect another person’s decision not to engage, respond or meet.",
            "Do not approach or continue contacting somebody who has made it clear that they do not wish to interact with you.",
        ],
    },
    {
        title: "11. Blocking and reporting",
        paragraphs: [
            "Lunar provides tools allowing users to block and report other users.",
            "Where you block another user, we may take reasonable measures to prevent that person from interacting with or being displayed to you through Lunar.",
            "Reports may be reviewed by Lunar and may result in warnings, restrictions, suspension or termination of an account where appropriate.",
            "You must not knowingly submit false or malicious reports.",
            "Where you believe somebody is in immediate danger or a crime is taking place, contact the appropriate emergency services rather than relying solely on Lunar’s reporting system.",
        ],
    },
    {
        title: "12. Venues",
        paragraphs: [
            "Locations displayed within Lunar may originate from third-party location providers or be added through Lunar’s venue functionality.",
            "A venue appearing within Lunar does not necessarily mean that the venue owns, operates, endorses, sponsors or is formally partnered with Lunar.",
            "Unless explicitly stated otherwise, references to third-party venues are provided solely to enable Lunar’s location functionality.",
            "Venue names, availability, opening status and other third-party information may change and we cannot guarantee that all venue information will always be complete or current.",
            "Users may report unsuitable or incorrect venues through the functionality provided.",
        ],
    },
    {
        title: "13. Founding Members, badges and rewards",
        paragraphs: [
            "Lunar may recognise users for contributing positively to the community.",
            "For example, a user who is the first eligible person to add a venue may receive a Founding Member badge associated with that location.",
            "Lunar may introduce additional badges, achievements or community recognition features.",
            "Unless expressly stated otherwise, badges have no monetary value, cannot be exchanged for cash and do not confer ownership of, or rights over, a venue or Lunar.",
            "We may remove badges obtained through manipulation, false check-ins, duplicate venues or other misuse.",
        ],
    },
    {
        title: "14. Acceptable use",
        paragraphs: ["You must not:"],
        list: [
            "use Lunar for unlawful purposes;",
            "harass, stalk, threaten or abuse another person;",
            "scrape or systematically collect information about Lunar users;",
            "attempt to circumvent privacy or safety controls;",
            "create fake accounts;",
            "manipulate location or check-in systems;",
            "interfere with Lunar’s security or operation;",
            "use automated systems or bots without our permission;",
            "attempt to identify or locate users who have chosen not to be visible to you; or",
            "use Lunar in a way that puts other users at unreasonable risk.",
        ],
    },
    {
        title: "15. Suspension and termination",
        paragraphs: [
            "We may restrict, suspend or terminate access to Lunar where we reasonably believe that a user has seriously or repeatedly breached these Terms, created a safety risk, acted unlawfully or misused the service.",
            "Where appropriate, we may investigate reports before taking action.",
            "Users may stop using Lunar and delete their account at any time.",
        ],
    },
    {
        title: "16. Availability of Lunar",
        paragraphs: [
            "We want Lunar to be available and reliable, but we cannot promise that it will always operate without interruption or error.",
            "We may need to maintain, update or modify Lunar from time to time.",
            "Features may also evolve as Lunar develops. Where a change materially affects users’ rights or obligations, we will provide appropriate information or notice.",
        ],
    },
    {
        title: "17. Third-party services",
        paragraphs: [
            "Lunar may rely on third-party services for functions such as maps, places, authentication, hosting, notifications and other technical infrastructure.",
            "Those services may be subject to their own terms and privacy policies.",
            "Lunar is not responsible for third-party services to the extent that matters are outside our reasonable control.",
        ],
    },
    {
        title: "18. Intellectual property",
        paragraphs: [
            "The Lunar name, branding, software, design and other intellectual property belonging to Somerhill Group Ltd or its licensors remain their property.",
            "These Terms give you permission to use Lunar for your personal use in accordance with these Terms. They do not transfer ownership of Lunar or its intellectual property to you.",
        ],
    },
    {
        title: "19. Our responsibility to you",
        paragraphs: [
            "We will provide Lunar with reasonable care and skill.",
            "Lunar is a platform for facilitating introductions between users. We do not control the conduct of users once they interact online or meet in person.",
            "Nothing in these Terms excludes or limits liability where doing so would be unlawful, including liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or your statutory consumer rights.",
            "To the extent permitted by law, we are not responsible for losses that were not reasonably foreseeable when you agreed to these Terms or losses resulting from circumstances outside our reasonable control.",
        ],
    },
    {
        title: "20. Privacy",
        paragraphs: [
            "Your privacy is important to us.",
            "Our Privacy Policy explains what personal information we collect, why we collect it, how location information is used, who information may be shared with and the rights available to you.",
            "You should read the Privacy Policy before using Lunar.",
        ],
    },
    {
        title: "21. Changes to these Terms",
        paragraphs: [
            "We may update these Terms where reasonably necessary, for example because Lunar changes, new features are introduced or legal requirements change.",
            "Where changes materially affect your rights or obligations, we will take reasonable steps to bring them to your attention before they take effect where required.",
        ],
    },
    {
        title: "22. Governing law",
        paragraphs: [
            "These Terms are governed by the laws of England and Wales.",
            "If you are a consumer resident elsewhere in the UK, you will retain any mandatory rights and protections available to you under the laws applicable where you live.",
            "Nothing in these Terms prevents you from exercising rights you have under applicable consumer law.",
        ],
    },
    {
        title: "23. Contact us",
        paragraphs: [
            "If you have questions about these Terms, Lunar or a safety concern, contact: hello@somerhillgroup.com",
        ],
    },
];

export default function Terms() {
    return (
        <LegalPage title="Terms of Use" intro={INTRO} sections={SECTIONS} prefix="terms" />
    );
}
